import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, Scale, Trash2, Gift, Copy, Check, Crown, Dumbbell, Shield, Target, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import UpgradeModal from '../components/UpgradeModal';
import DisclaimerModal from '../components/DisclaimerModal';
import HamburgerMenu from '../components/HamburgerMenu';

const Settings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    age: '',
    height: '',
    height_feet: '',
    height_inches: '',
    starting_weight: '',
    goal_weight: '',
    weight_unit: 'lbs',
    weight_calibration: '',
    injuries: '',
    weak_point: 'none'
  });
  const [weightLogs, setWeightLogs] = useState([]);
  const [newWeight, setNewWeight] = useState({
    weight: '',
    notes: ''
  });
  const [loading, setLoading] = useState(true);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [individualBilling, setIndividualBilling] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [referralActionLoading, setReferralActionLoading] = useState(false);
  const [giftableFriends, setGiftableFriends] = useState([]);
  const [showGiftPicker, setShowGiftPicker] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [warmupDuration, setWarmupDuration] = useState(5);
  const [cooldownType, setCooldownType] = useState('none');
  const [cooldownDuration, setCooldownDuration] = useState(5);
  const [fitnessGoal, setFitnessGoal] = useState('maintain');
  const [privacySettings, setPrivacySettings] = useState({
    profile_visibility: 'public',
    show_stats: true,
    allow_battle_requests: true
  });
  const [availableEquipment] = useState([
    'dumbbells', 'barbell', 'bench', 'pullup_bar', 'dip_bar', 'rings', 'resistance_bands',
    'kettlebell', 'yoga_mat', 'jump_rope', 'foam_roller', 'medicine_ball',
    'treadmill', 'stationary_bike', 'rowing_machine'
  ]);

  useEffect(() => {
    loadSettings();
    loadPremiumStatus();
    axios.get(`${API}/subscriptions/status`).then((r) => setIndividualBilling(r.data)).catch(() => {});
  }, []);

  const loadPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API}/premium/status`);
      setPremiumStatus(response.data);
    } catch (error) {
      console.error('Error loading premium status:', error);
    }
  };

  const manageIndividualBilling = async () => {
    try {
      const r = await axios.post(`${API}/subscriptions/portal`, { owner_type: 'individual' });
      if (r.data.url) window.location.assign(r.data.url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to open billing portal.');
    }
  };

  const handleCopyReferralCode = () => {
    if (premiumStatus?.referral_code) {
      const referralLink = `${window.location.origin}?ref=${premiumStatus.referral_code}`;
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApplyReferralCredit = async () => {
    try {
      setReferralActionLoading(true);
      const response = await axios.post(`${API}/referrals/apply-credit`);
      toast.success(response.data?.message || 'Premium days applied!');
      setShowGiftPicker(false);
      await loadPremiumStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not apply referral days.');
    } finally {
      setReferralActionLoading(false);
    }
  };

  const handleOpenGiftPicker = async () => {
    try {
      setReferralActionLoading(true);
      const response = await axios.get(`${API}/referrals/giftable-friends`);
      setGiftableFriends(response.data?.friends || []);
      setShowGiftPicker(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load eligible friends.');
    } finally {
      setReferralActionLoading(false);
    }
  };

  const handleGiftReferralCredit = async (friendUserId) => {
    try {
      setReferralActionLoading(true);
      const response = await axios.post(`${API}/referrals/gift-credit`, {
        friend_user_id: friendUserId
      });
      toast.success(response.data?.message || 'Premium days gifted!');
      setShowGiftPicker(false);
      setGiftableFriends([]);
      await loadPremiumStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not gift referral days.');
    } finally {
      setReferralActionLoading(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load profile
      const profileResponse = await axios.get(`${API}/user/profile`);
      const totalInches = profileResponse.data.height || 0;
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      
      setProfile({
        age: profileResponse.data.age || '',
        height: profileResponse.data.height || '',
        height_feet: feet || '',
        height_inches: inches || '',
        starting_weight: profileResponse.data.starting_weight || '',
        goal_weight: profileResponse.data.goal_weight || '',
        weight_unit: profileResponse.data.weight_unit || 'lbs',
        weight_calibration: profileResponse.data.weight_calibration || '',
        injuries: profileResponse.data.injuries || '',
        weak_point: profileResponse.data.weak_point || 'none'
      });
      const loadedEquipment = (
        profileResponse.data.home_equipment || []
      ).map((item) =>
        item === 'pull_up_bar'
          ? 'pullup_bar'
          : item
      );

      setEquipment(loadedEquipment);
      setWarmupDuration(profileResponse.data.warmup_duration || 5);
      setCooldownType(profileResponse.data.cooldown_type || 'none');
      setCooldownDuration(profileResponse.data.cooldown_duration || 5);
      setFitnessGoal(profileResponse.data.fitness_goal || 'maintain');
      
      // Handle privacy settings with safe defaults for older profiles
      const privacyData = profileResponse.data.privacy_settings || {};
      setPrivacySettings({
        profile_visibility: privacyData.profile_visibility || profileResponse.data.profile_visibility || 'public',
        show_stats: privacyData.show_stats !== undefined ? privacyData.show_stats : (profileResponse.data.show_stats !== false),
        allow_battle_requests: privacyData.allow_battle_requests !== undefined ? privacyData.allow_battle_requests : (profileResponse.data.allow_battle_requests !== false)
      });

      // Load weight logs (non-blocking - might not exist)
      try {
        const logsResponse = await axios.get(`${API}/weight/logs?limit=10`);
        setWeightLogs(logsResponse.data || []);
      } catch (weightError) {
        console.log('No weight logs available:', weightError);
        setWeightLogs([]);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // Convert feet and inches to total inches
      const totalInches = profile.height_feet && profile.height_inches 
        ? (parseInt(profile.height_feet) * 12) + parseInt(profile.height_inches)
        : null;
      
      const updateData = {
        age: profile.age ? parseInt(profile.age) : null,
        height: totalInches,
        starting_weight: profile.starting_weight ? parseFloat(profile.starting_weight) : null,
        goal_weight: profile.goal_weight ? parseFloat(profile.goal_weight) : null,
        weight_unit: profile.weight_unit,
        weight_calibration: profile.weight_calibration || null,
        injuries: profile.injuries || null,
        weak_point: profile.weak_point || 'none'
      };

      await axios.put(`${API}/user/profile`, updateData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleWeightLog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/weight/log`, {
        date: new Date().toISOString(),
        weight: parseFloat(newWeight.weight),
        notes: newWeight.notes
      });

      toast.success('Weight logged successfully!');
      setNewWeight({ weight: '', notes: '' });
      loadSettings();
    } catch (error) {
      toast.error('Failed to log weight');
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/weight/log/${logId}`);
      toast.success('Weight log deleted');
      loadSettings();
    } catch (error) {
      toast.error('Failed to delete log');
    }
  };


  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setChangingPassword(true);
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black flex items-center justify-center">
        <div className="text-blood-400 text-xl font-bold tracking-wider">LOADING SETTINGS...</div>
      </div>
    );
  }

  const handleEquipmentToggle = async (item) => {
    const newEquipment = equipment.includes(item)
      ? equipment.filter(e => e !== item)
      : [...equipment, item];
    
    try {
      await axios.put(`${API}/user/equipment`, newEquipment);
      setEquipment(newEquipment);
      toast.success('Equipment updated!');
    } catch (error) {
      toast.error('Failed to update equipment');
    }
  };

  const handleFitnessGoalChange = async (goal) => {
    try {
      await axios.put(`${API}/user/fitness-goal`, { fitness_goal: goal });
      setFitnessGoal(goal);
      toast.success('Fitness goal updated! New workouts will reflect this change.');
    } catch (error) {
      toast.error('Failed to update fitness goal');
    }
  };

  const handleWarmupDurationChange = async (minutes) => {
    try {
      await axios.put(`${API}/user/warmup-duration`, { warmup_duration: minutes });
      setWarmupDuration(minutes);
      toast.success(`Warm-up set to ${minutes} minutes`);
    } catch (error) {
      toast.error('Failed to update warm-up duration');
    }
  };

  const handleCooldownChange = async (type, duration) => {
    const newType = type !== undefined ? type : cooldownType;
    const newDuration = duration !== undefined ? duration : cooldownDuration;
    try {
      await axios.put(`${API}/user/cooldown-preference`, { cooldown_type: newType, cooldown_duration: newDuration });
      if (type !== undefined) setCooldownType(newType);
      if (duration !== undefined) setCooldownDuration(newDuration);
      if (newType === 'none') {
        toast.success('Cool-down disabled');
      } else {
        toast.success(`Cool-down updated`);
      }
    } catch (error) {
      toast.error('Failed to update cool-down preference');
    }
  };

  const handlePrivacyUpdate = async () => {
    try {
      await axios.put(`${API}/user/privacy`, privacySettings);
      toast.success('Privacy settings updated!');
    } catch (error) {
      toast.error('Failed to update privacy settings');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20 overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-5xl mx-auto px-4 py-4 animate-fade-in">
        {/* Back Button */}
        <button onClick={() => navigate('/hub')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 tracking-wider">SETTINGS</h1>
        </div>

        {/* Premium Status Banner */}
        {premiumStatus && (
          <Card className="glass p-6 mb-6 border border-stone-800">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Crown className={`w-8 h-8 ${premiumStatus.is_premium ? 'text-blood-500' : 'text-stone-600'}`} />
                <div>
                  <h2 className="text-xl font-bold text-blood-400 tracking-wider">
                    {premiumStatus.is_premium ? 'PREMIUM ACCOUNT' : 'FREE ACCOUNT'}
                  </h2>
                  {premiumStatus.trial_active && (
                    <p className="text-blood-500 text-sm font-semibold">
                      Trial: {premiumStatus.days_remaining} days remaining
                    </p>
                  )}
                </div>
              </div>
              {['active', 'trialing'].includes(individualBilling?.status) && (
                <Button onClick={manageIndividualBilling} className="bg-stone-800 text-white">Manage Billing</Button>
              )}
              {!premiumStatus.paid_active && !['active', 'trialing'].includes(individualBilling?.status) && (
                <Button
                  onClick={() => setShowUpgradeModal(true)}
                  className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold px-6 py-3 rounded-xl tracking-wider"
                  data-testid="upgrade-now-btn"
                >
                  {premiumStatus.is_premium ? 'SUBSCRIBE NOW' : 'UPGRADE NOW'}
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Referral Card */}
        {premiumStatus && (
          <Card className="glass p-8 mb-6 bg-gradient-to-r from-blood-900/20 to-blood-800/20 border border-blood-800" data-testid="referral-credit-card">
            <div className="flex items-center gap-3 mb-4">
              <Gift className="w-6 h-6 text-blood-500" />
              <h2 className="text-2xl font-bold text-blood-400 tracking-wider">REFER FRIENDS, EARN PREMIUM DAYS</h2>
            </div>
            <p className="text-stone-400 mb-4">
              Earn <span className="font-bold text-blood-400">7 Premium days</span> when a referred friend completes account setup. Stack them, use them yourself, or gift them to an eligible friend.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={`${window.location.origin}?ref=${premiumStatus.referral_code}`}
                readOnly
                className="flex-1 glass text-white px-4 py-3 rounded-lg border border-stone-700"
              />
              <Button
                onClick={handleCopyReferralCode}
                className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white px-6 rounded-lg font-bold"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass p-3 rounded-lg text-center border border-stone-800">
                <p className="text-2xl font-bold text-blood-400">{premiumStatus.referral_count || 0}</p>
                <p className="text-stone-400 text-sm uppercase tracking-wider">Qualified Referrals</p>
              </div>
              <div className="glass p-3 rounded-lg text-center border border-stone-800">
                <p className="text-2xl font-bold text-blood-400">{(premiumStatus.referral_count || 0) * 7}</p>
                <p className="text-stone-400 text-sm uppercase tracking-wider">Days Earned</p>
              </div>
              <div className="glass p-3 rounded-lg text-center border border-stone-800">
                <p className="text-2xl font-bold text-blood-500" data-testid="available-referral-days">{premiumStatus.available_referral_days || 0}</p>
                <p className="text-stone-400 text-sm uppercase tracking-wider">Days Available</p>
              </div>
            </div>

            {(premiumStatus.available_referral_days || 0) >= 7 && (
              <div className="mt-5 border-t border-blood-900/60 pt-5" data-testid="referral-credit-actions">
                <p className="text-white font-bold mb-3">You have 7 Premium days ready to use.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    onClick={handleApplyReferralCredit}
                    disabled={referralActionLoading}
                    className="bg-blood-700 hover:bg-blood-800 text-white font-bold"
                    data-testid="apply-referral-credit-btn"
                  >
                    APPLY 7 DAYS TO MY ACCOUNT
                  </Button>
                  <Button
                    onClick={handleOpenGiftPicker}
                    disabled={referralActionLoading}
                    variant="outline"
                    className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-bold"
                    data-testid="gift-referral-credit-btn"
                  >
                    GIFT 7 DAYS TO A FRIEND
                  </Button>
                </div>

                {showGiftPicker && (
                  <div className="mt-4 glass border border-stone-700 rounded-lg p-4" data-testid="referral-gift-picker">
                    <p className="text-white font-semibold mb-3">Choose an eligible friend</p>
                    {giftableFriends.length === 0 ? (
                      <p className="text-stone-400 text-sm">No eligible friends are available to receive Premium days right now.</p>
                    ) : (
                      <div className="space-y-2">
                        {giftableFriends.map((friend) => (
                          <button
                            key={friend.id}
                            onClick={() => handleGiftReferralCredit(friend.id)}
                            disabled={referralActionLoading}
                            className="w-full flex items-center justify-between glass border border-stone-700 hover:border-blood-700 rounded-lg px-4 py-3 text-left"
                          >
                            <span className="text-white font-semibold">{friend.username}</span>
                            <span className="text-blood-400 text-sm font-bold">GIFT 7 DAYS</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile Settings */}
          <Card className="glass p-8 border border-stone-800">
            <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
              <User className="w-6 h-6 mr-3 text-blood-500" />
              PROFILE INFORMATION
            </h2>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Age</label>
                <input
                  type="number"
                  value={profile.age}
                  onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="Enter your age"
                />
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">
                  Height
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-stone-500 text-xs mb-1 block">Feet</label>
                    <input
                      type="number"
                      min="0"
                      max="8"
                      value={profile.height_feet}
                      onChange={(e) => setProfile({ ...profile, height_feet: e.target.value })}
                      className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                      placeholder="5"
                    />
                  </div>
                  <div>
                    <label className="text-stone-500 text-xs mb-1 block">Inches</label>
                    <input
                      type="number"
                      min="0"
                      max="11"
                      value={profile.height_inches}
                      onChange={(e) => setProfile({ ...profile, height_inches: e.target.value })}
                      className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                      placeholder="8"
                    />
                  </div>
                </div>
                <p className="text-stone-500 text-xs mt-1">
                  Example: 5&apos; 8&quot; (5 feet 8 inches)
                </p>
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Weight Unit</label>
                <select
                  value={profile.weight_unit}
                  onChange={(e) => setProfile({ ...profile, weight_unit: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                >
                  <option value="lbs">Pounds (lbs)</option>
                  <option value="kg">Kilograms (kg)</option>
                </select>
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">
                  Starting Weight ({profile.weight_unit})
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={profile.starting_weight}
                  onChange={(e) => setProfile({ ...profile, starting_weight: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="Your starting weight"
                />
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">
                  Goal Weight ({profile.weight_unit})
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={profile.goal_weight}
                  onChange={(e) => setProfile({ ...profile, goal_weight: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="Your target weight"
                />
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Weight Calibration</label>
                <p className="text-stone-600 text-xs mb-2">How should we calibrate your suggested weights?</p>
                <select
                  value={profile.weight_calibration}
                  onChange={(e) => setProfile({ ...profile, weight_calibration: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  data-testid="settings-weight-calibration"
                >
                  <option value="">Select calibration</option>
                  <option value="lighter">Lighter range</option>
                  <option value="standard">Standard range</option>
                  <option value="heavier">Heavier range</option>
                </select>
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Injuries / Limitations</label>
                <input
                  type="text"
                  value={profile.injuries}
                  onChange={(e) => setProfile({ ...profile, injuries: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="e.g., bad knees, shoulder injury, lower back pain"
                  data-testid="settings-injuries"
                />
                <p className="text-stone-600 text-xs mt-1">The AI will avoid exercises that could aggravate these conditions</p>
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Weak Point Emphasis</label>
                <select
                  value={profile.weak_point}
                  onChange={(e) => setProfile({ ...profile, weak_point: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  data-testid="settings-weak-point"
                >
                  <option value="none">None</option>
                  <option value="chest">Chest</option>
                  <option value="back">Back</option>
                  <option value="shoulders">Shoulders</option>
                  <option value="arms">Arms</option>
                  <option value="legs">Legs</option>
                  <option value="glutes">Glutes</option>
                  <option value="core">Core</option>
                </select>
                <p className="text-stone-600 text-xs mt-1">Your plans will add extra volume for this muscle group</p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-3 rounded-xl tracking-wider"
              >
                SAVE PROFILE
              </Button>
            </form>
          </Card>

          {/* Weight Logging */}
          <div className="space-y-6">
            <Card className="glass p-8 border border-stone-800">
              <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
                <Scale className="w-6 h-6 mr-3 text-blood-500" />
                LOG WEIGHT
              </h2>
              <form onSubmit={handleWeightLog} className="space-y-4">
                <div>
                  <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">
                    Weight ({profile.weight_unit})
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newWeight.weight}
                    onChange={(e) => setNewWeight({ ...newWeight, weight: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                    placeholder="Enter current weight"
                    required
                  />
                </div>

                <div>
                  <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Notes (optional)</label>
                  <textarea
                    value={newWeight.notes}
                    onChange={(e) => setNewWeight({ ...newWeight, notes: e.target.value })}
                    className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                    placeholder="Feeling great today!"
                    rows="3"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-3 rounded-xl tracking-wider"
                >
                  LOG WEIGHT
                </Button>
              </form>
            </Card>

            {/* Weight History */}
            <Card className="glass p-8 border border-stone-800">
              <h2 className="text-xl font-bold text-blood-400 mb-4 tracking-wider">RECENT ENTRIES</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {weightLogs.length > 0 ? (
                  weightLogs.map((log) => (
                    <div key={log.id} className="glass p-4 rounded-lg flex justify-between items-center border border-stone-800">
                      <div>
                        <p className="text-blood-400 font-bold">
                          {log.weight} {profile.weight_unit}
                        </p>
                        <p className="text-stone-400 text-sm">
                          {new Date(log.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                        {log.notes && (
                          <p className="text-stone-500 text-xs mt-1">{log.notes}</p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleDeleteLog(log.id)}
                        variant="outline"
                        className="glass border-blood-900 text-blood-400 hover:bg-blood-950/50 font-semibold"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-stone-400 text-center py-8 uppercase tracking-wider">NO WEIGHT ENTRIES YET</p>
                )}
              </div>
            </Card>
          </div>


          {/* Password Change Card */}
          <Card className="glass p-8 border border-stone-800 lg:col-span-2">
            <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
              <User className="w-6 h-6 mr-3 text-blood-500" />
              CHANGE PASSWORD
            </h2>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-2xl">
              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="Enter current password"
                  required
                />
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="text-stone-400 text-sm mb-2 block uppercase tracking-wider">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full glass text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blood-700 border border-stone-700"
                  placeholder="Confirm new password"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={changingPassword}
                className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold py-3 px-8 rounded-xl tracking-wider"
              >
                {changingPassword ? 'CHANGING...' : 'CHANGE PASSWORD'}
              </Button>
            </form>
          </Card>

          {/* Fitness Goal Card */}
          <Card className="glass p-8 border border-stone-800 lg:col-span-2">
            <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
              <Target className="w-6 h-6 mr-3 text-blood-500" />
              FITNESS GOAL
            </h2>
            <p className="text-stone-400 mb-6">
              Select your fitness goal. Workouts will adjust sets, reps, and rest periods accordingly.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => handleFitnessGoalChange('bulk')}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  fitnessGoal === 'bulk'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-lg font-bold text-blood-400 mb-2">💪 BULK</h3>
                <p className="text-stone-400 text-sm mb-2">Build muscle</p>
                <p className="text-xs text-stone-500">4-8 reps • Heavy weights</p>
              </button>

              <button
                onClick={() => handleFitnessGoalChange('tone')}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  fitnessGoal === 'tone'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-lg font-bold text-blood-400 mb-2">✨ TONE</h3>
                <p className="text-stone-400 text-sm mb-2">Definition</p>
                <p className="text-xs text-stone-500">12-15 reps • Moderate</p>
              </button>

              <button
                onClick={() => handleFitnessGoalChange('lose_weight')}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  fitnessGoal === 'lose_weight'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-lg font-bold text-blood-400 mb-2">🔥 LOSE WEIGHT</h3>
                <p className="text-stone-400 text-sm mb-2">Fat loss</p>
                <p className="text-xs text-stone-500">15-20 reps • Light</p>
              </button>

              <button
                onClick={() => handleFitnessGoalChange('maintain')}
                className={`glass p-6 rounded-xl transition-all border-2 ${
                  fitnessGoal === 'maintain'
                    ? 'border-blood-600 bg-blood-950/30'
                    : 'border-stone-700 hover:border-blood-800'
                }`}
              >
                <h3 className="text-lg font-bold text-blood-400 mb-2">⚖️ MAINTAIN</h3>
                <p className="text-stone-400 text-sm mb-2">General fitness</p>
                <p className="text-xs text-stone-500">8-12 reps • Balanced</p>
              </button>
            </div>
          </Card>

          {/* Equipment Manager Card */}
          <Card className="glass p-8 border border-stone-800 lg:col-span-2">
            <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
              <Dumbbell className="w-6 h-6 mr-3 text-blood-500" />
              HOME GYM EQUIPMENT
            </h2>
            <p className="text-stone-400 mb-6">
              Update your available equipment. Workouts will be adjusted to use what you have.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {availableEquipment.map((item) => (
                <button
                  key={item}
                  onClick={() => handleEquipmentToggle(item)}
                  className={`p-4 rounded-lg border-2 transition-all text-center ${
                    equipment.includes(item)
                      ? 'bg-blood-900/30 border-blood-600 text-blood-400'
                      : 'glass border-stone-700 text-stone-400 hover:border-stone-600'
                  }`}
                >
                  <div className="text-2xl mb-2">
                    {item === 'dumbbells' && '🔩'}
                    {item === 'barbell' && '🏋️‍♂️'}
                    {item === 'bench' && '🛏️'}
                    {item === 'pull_up_bar' && '🦾'}
                    {item === 'resistance_bands' && '〰️'}
                    {item === 'kettlebell' && '⚫'}
                    {item === 'yoga_mat' && '🧘'}
                    {item === 'jump_rope' && '🪢'}
                    {item === 'foam_roller' && '🌯'}
                    {item === 'medicine_ball' && '🏀'}
                    {item === 'treadmill' && '🏃'}
                    {item === 'stationary_bike' && '🚴'}
                    {item === 'rowing_machine' && '🚣'}
                  </div>
                  <span className="text-xs uppercase tracking-wide font-semibold block">
                    {item.replace('_', ' ')}
                  </span>
                  {equipment.includes(item) && (
                    <span className="text-xs text-blood-400 mt-1 block">✓ Selected</span>
                  )}
                </button>
              ))}
            </div>
            {equipment.length > 0 && (
              <p className="text-blood-400 text-sm mt-4 font-semibold">
                {equipment.length} item(s) selected
              </p>
            )}

            {/* Warm-up Duration Preference */}
            {equipment.some(e => ['treadmill', 'stationary_bike', 'rowing_machine'].includes(e)) && (
              <div className="mt-6 pt-6 border-t border-stone-800" data-testid="warmup-duration-section">
                <h3 className="text-lg font-bold text-stone-200 mb-2 tracking-wider">
                  WARM-UP DURATION
                </h3>
                <p className="text-stone-400 text-sm mb-4">
                  How long do you want your cardio warm-up to be?
                </p>
                <div className="flex gap-3">
                  {[5, 10, 15].map((mins) => (
                    <button
                      key={mins}
                      data-testid={`warmup-duration-${mins}`}
                      onClick={() => handleWarmupDurationChange(mins)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all font-bold text-center ${
                        warmupDuration === mins
                          ? 'bg-blood-900/30 border-blood-600 text-blood-400'
                          : 'glass border-stone-700 text-stone-400 hover:border-stone-600'
                      }`}
                    >
                      <span className="text-xl block">{mins}</span>
                      <span className="text-xs uppercase tracking-wide">min</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cool-down Preference */}
            <div className="mt-6 pt-6 border-t border-stone-800" data-testid="cooldown-section">
              <h3 className="text-lg font-bold text-stone-200 mb-2 tracking-wider">
                COOL-DOWN
              </h3>
              <p className="text-stone-400 text-sm mb-4">
                Add a cool-down to the end of every workout.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { value: 'none', label: 'None', icon: '—' },
                  { value: 'stretching', label: 'Stretching', icon: '🧘' },
                  { value: 'foam_rolling', label: 'Foam Rolling', icon: '🌯' },
                  { value: 'light_cardio', label: 'Light Cardio', icon: '🚶' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`cooldown-type-${opt.value}`}
                    onClick={() => handleCooldownChange(opt.value, undefined)}
                    className={`py-3 px-3 rounded-lg border-2 transition-all text-center ${
                      cooldownType === opt.value
                        ? 'bg-blood-900/30 border-blood-600 text-blood-400'
                        : 'glass border-stone-700 text-stone-400 hover:border-stone-600'
                    }`}
                  >
                    <span className="text-xl block">{opt.icon}</span>
                    <span className="text-xs uppercase tracking-wide font-semibold">{opt.label}</span>
                  </button>
                ))}
              </div>
              {cooldownType !== 'none' && (
                <div className="flex gap-3">
                  {[5, 10, 15].map((mins) => (
                    <button
                      key={mins}
                      data-testid={`cooldown-duration-${mins}`}
                      onClick={() => handleCooldownChange(undefined, mins)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all font-bold text-center ${
                        cooldownDuration === mins
                          ? 'bg-blood-900/30 border-blood-600 text-blood-400'
                          : 'glass border-stone-700 text-stone-400 hover:border-stone-600'
                      }`}
                    >
                      <span className="text-xl block">{mins}</span>
                      <span className="text-xs uppercase tracking-wide">min</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Disclaimer Button Card */}
          <Card className="glass p-8 border border-stone-800">
            <h2 className="text-2xl font-bold text-blood-400 mb-4 flex items-center tracking-wider">
              <Shield className="w-6 h-6 mr-3 text-blood-500" />
              DISCLAIMER
            </h2>
            <p className="text-stone-400 mb-6">
              Review the important safety and usage information for Anvil & Ash.
            </p>
            <Button
              onClick={() => setShowDisclaimer(true)}
              variant="outline"
              className="glass border-blood-700 text-blood-400 hover:bg-blood-950/50 font-semibold"
            >
              VIEW DISCLAIMER
            </Button>
          </Card>

          {/* Privacy Settings Card */}
          <Card className="glass p-8 border border-stone-800 lg:col-span-2">
            <h2 className="text-2xl font-bold text-blood-400 mb-6 flex items-center tracking-wider">
              <Shield className="w-6 h-6 mr-3 text-blood-500" />
              PRIVACY & SOCIAL SETTINGS
            </h2>
            <p className="text-stone-400 mb-6">
              Control who can see your profile and interact with you.
            </p>

            <div className="space-y-6">
              {/* Profile Visibility */}
              <div>
                <label className="text-blood-400 uppercase tracking-wider font-semibold text-sm mb-3 block">
                  Profile Visibility
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    onClick={() => setPrivacySettings({...privacySettings, profile_visibility: 'public'})}
                    className={`glass p-4 rounded-xl transition-all border-2 ${
                      privacySettings.profile_visibility === 'public'
                        ? 'border-blood-600 bg-blood-950/30'
                        : 'border-stone-700 hover:border-blood-800'
                    }`}
                  >
                    <h3 className="text-lg font-bold text-blood-400 mb-1">🌍 Public</h3>
                    <p className="text-xs text-stone-400">Anyone can find and view your profile</p>
                  </button>

                  <button
                    onClick={() => setPrivacySettings({...privacySettings, profile_visibility: 'friends_only'})}
                    className={`glass p-4 rounded-xl transition-all border-2 ${
                      privacySettings.profile_visibility === 'friends_only'
                        ? 'border-blood-600 bg-blood-950/30'
                        : 'border-stone-700 hover:border-blood-800'
                    }`}
                  >
                    <h3 className="text-lg font-bold text-blood-400 mb-1">👥 Friends Only</h3>
                    <p className="text-xs text-stone-400">Only friends can view your profile</p>
                  </button>

                  <button
                    onClick={() => setPrivacySettings({...privacySettings, profile_visibility: 'private'})}
                    className={`glass p-4 rounded-xl transition-all border-2 ${
                      privacySettings.profile_visibility === 'private'
                        ? 'border-blood-600 bg-blood-950/30'
                        : 'border-stone-700 hover:border-blood-800'
                    }`}
                  >
                    <h3 className="text-lg font-bold text-blood-400 mb-1">🔒 Private</h3>
                    <p className="text-xs text-stone-400">Hidden from search and discovery</p>
                  </button>
                </div>
              </div>

              {/* Show Stats Toggle */}
              <div className="flex items-center justify-between glass border border-stone-800 rounded-lg p-4">
                <div>
                  <div className="font-bold text-blood-400">Show Workout Stats</div>
                  <div className="text-sm text-stone-400">Display your workout count and battle records on your profile</div>
                </div>
                <button
                  onClick={() => setPrivacySettings({...privacySettings, show_stats: !privacySettings.show_stats})}
                  className={`w-14 h-8 rounded-full transition-all ${
                    privacySettings.show_stats ? 'bg-blood-700' : 'bg-stone-700'
                  }`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                    privacySettings.show_stats ? 'translate-x-7' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>

              {/* Battle Requests Toggle */}
              <div className="flex items-center justify-between glass border border-stone-800 rounded-lg p-4">
                <div>
                  <div className="font-bold text-blood-400">Allow Battle Requests</div>
                  <div className="text-sm text-stone-400">Let non-friends send you battle challenges</div>
                </div>
                <button
                  onClick={() => setPrivacySettings({...privacySettings, allow_battle_requests: !privacySettings.allow_battle_requests})}
                  className={`w-14 h-8 rounded-full transition-all ${
                    privacySettings.allow_battle_requests ? 'bg-blood-700' : 'bg-stone-700'
                  }`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                    privacySettings.allow_battle_requests ? 'translate-x-7' : 'translate-x-1'
                  }`}></div>
                </button>
              </div>

              <Button
                onClick={handlePrivacyUpdate}
                className="w-full bg-blood-700 hover:bg-blood-800 font-bold py-3 tracking-wider"
              >
                SAVE PRIVACY SETTINGS
              </Button>
            </div>
          </Card>

          {/* Legal Links */}
          <div className="flex justify-center gap-6 mt-4 mb-8">
            <a href="/privacy-policy" className="text-stone-500 hover:text-stone-300 text-xs transition-colors" data-testid="settings-privacy-link">
              Privacy Policy
            </a>
            <a href="/terms-of-service" className="text-stone-500 hover:text-stone-300 text-xs transition-colors" data-testid="settings-terms-link">
              Terms of Service
            </a>
          </div>

        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      {showDisclaimer && <DisclaimerModal onAccept={() => setShowDisclaimer(false)} onClose={() => setShowDisclaimer(false)} />}
    </div>
  );
};

export default Settings;
